import { Action, Dispatch, MiddlewareAPI } from 'redux';

export interface SiteAnalyticsAction {
    meta: {
        analytics: {
            action: string;
            category: string;
            label?: string;
            dimensions?: Record<string, unknown>;
            metrics?: Record<string, unknown>;
            additionalProperties?: Record<string, unknown>;
            requestPath?: string;
        };
    };
}

export interface DefaultAttributes extends Record<string, unknown> {
    dimensions?: Record<string, unknown>;
}

/**
 * Redux middleware that allows seamless integration of Datadog RUM events with redux actions.
 *
 * It tracks actions that have an 'analytics' object inside the 'meta' object of the action
 *
 * @return {function} Result from next steps of the middleware
 *
 * @example
 * Use it with the configureStore helper to add it to the middleware of the app:
 *
 * this._store = configureStore({
 *     reducer,
 *     initialState: {},
 *     middleware: [datadogRealUserMonitoringMiddleware()],
 * });
 *
 * If you would like to add some (default) props globally to your app, provide a
 * getDefaults arg to the middleware:
 *
 * datadogRealUserMonitoringMiddleware((state) => ({
 *     category: 'myGACat',
 *     dimensions: {
 *         sessionId: state.sessionId,
 *     },
 * })
 *
 * Then simply dispatch an action with `meta.analytics` information:
 *
 * dispatch({
 *     type: MY_ACTION,
 *     payload: {...},
 *     meta: {
 *         analytics: {
 *             action: MY_ACTION_GA_ACTION,
 *             category: MY_ACTION_CATEGORY,
 *             label: A_LABEL,
 *             dimensions: {
 *                 isFirstOrder: true,
 *             },
 *         }
 *     }
 * });
 *
 * Only `action` and `category` are required. The user and event dimensions will be populated automatically
 * from the state, if available.
 */
export const realUserMonitoringReduxMiddleware = <
    State extends Record<string, unknown>
>(
    getDefaults: (state: State) => DefaultAttributes = () => ({}),
) => ({ getState }: MiddlewareAPI<State>) => (
    next: Dispatch<State>,
): Dispatch<State> => <TheAction extends Action>(action: TheAction) => {
    const result = next(action);

    const siteAnalyticsAction = (action as unknown) as SiteAnalyticsAction;

    // We have to ignore any non-analytics action
    if (!siteAnalyticsAction.meta || !siteAnalyticsAction.meta.analytics) {
        return result;
    }

    const state = getState();
    const defaults = getDefaults(state);

    const payload = {
        ...defaults,
        ...siteAnalyticsAction.meta.analytics,
        dimensions: {
            ...(defaults.dimensions || {}),
            ...(siteAnalyticsAction.meta.analytics.dimensions || {}),
        },
    };

    if (
        typeof window !== 'undefined' &&
        window.DD_RUM &&
        window.DD_RUM.addAction
    ) {
        window.DD_RUM.addAction(payload.action, payload);
    }

    return result;
};
